/** The headline, one per influence level (platform design §11.5: scores at
 *  different levels never share a figure). */

import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { PageSection } from '../../../chrome/page';
import { EmptyState, QueryStates } from '../../../chrome/state';
import type { ApiFailureError } from '../../../lib/http/errors';
import type { Window } from '../api/queries';
import type { Sentiment } from '../api/schemas';
import { LatestPoint } from './LatestPoint';
import { st2 } from './messages';
import { SentimentCard } from './SentimentCard';
import { byLevel, type LevelSeries } from './series';

function titleOf(series: LevelSeries, levels: number): string | undefined {
  if (series.level !== null) return st2('level.named', { level: series.level });
  return levels > 1 ? st2('level.none') : undefined;
}

export function SeriesView(props: { sentiment: Sentiment; window: Window }): ReactNode {
  const levels = byLevel(props.sentiment.points);
  if (levels.length === 1) return <LatestPoint series={levels[0]!} window={props.window} />;
  return (
    <div className="flex flex-col gap-4">
      {levels.map((series) => (
        <PageSection key={series.level ?? ''} title={titleOf(series, levels.length)}>
          <LatestPoint series={series} window={props.window} />
        </PageSection>
      ))}
    </div>
  );
}

export function SeriesSection(props: {
  query: UseQueryResult<Sentiment, ApiFailureError>;
  window: Window;
}): ReactNode {
  const frame = (state: ReactNode) => (
    <SentimentCard title={st2('headline.title')}>{state}</SentimentCard>
  );
  if (props.query.status === 'success' && props.query.data.points.length > 0) {
    return <SeriesView sentiment={props.query.data} window={props.window} />;
  }
  return frame(
    <QueryStates
      query={props.query}
      isEmpty={(data) => data.points.length === 0}
      empty={<EmptyState title={st2('empty')} description={st2('emptyHint')} />}
    >
      {() => null}
    </QueryStates>,
  );
}
